import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../config/axiosConfig";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { convertToFormData } from "../config/convertToFormData";
import { actionType, listOfDocuments, userRoles } from "../utils/constant";
import { hasPermission } from "../utils/utilityFunctions";
import { useSelector } from "react-redux";

const MAX_FILES_PER_DOCUMENT = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Helper function to format document names
const formatKeyWord = (str) => {
  return str.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
    return str.toUpperCase();
  });
};

const createValidationSchema = (listOfDocuments) => {
  const documentValidation = {};

  listOfDocuments.forEach((docName) => {
    documentValidation[docName] = Yup.object()
      .shape({
        documents_uploaded: Yup.array()
          .max(
            MAX_FILES_PER_DOCUMENT,
            `Maximum ${MAX_FILES_PER_DOCUMENT} files allowed for ${formatKeyWord(
              docName
            )}`
          )
          .of(
            Yup.object().shape({
              fileName: Yup.string().required(),
              file: Yup.mixed().required(),
              id: Yup.string().required(),
            })
          ),
        signature_required: Yup.boolean(),
      })
      .required();
  });

  return Yup.object().shape({
    documents: Yup.object()
      .shape(documentValidation)
      .required()
      .test(
        "at-least-one-file",
        "At least one document must have files uploaded",
        function (documents) {
          const hasFiles = Object.values(documents).some(
            (doc) => doc.documents_uploaded && doc.documents_uploaded.length > 0
          );
          return hasFiles;
        }
      ),
    docStatus: Yup.boolean(),
  });
};

export default function useDocumentController({
  docStatus,
  getRFIDetails,
  role,
}) {
  const { rfinumber: rfi_id } = useParams();
  const { profile } = useSelector((state) => state.profile);
  const navigate = useNavigate();

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [file, setFile] = useState(null);

  // Esign require states
  const [startEsign, setStartEsign] = useState(false);
  const [esignUrlLoading, setEsignUrlLoading] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [signatures, setSignatures] = useState([]);
  const [error, setError] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [pdfLoadingError, setPdfLoadingError] = useState(false);
  const [allDocSigned, setAllDocSigned] = useState(false);

  const validationSchema = createValidationSchema(listOfDocuments);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // Handle form submission logic here
    for (const [key, value] of Object.entries(values.documents)) {
      // Process each item sequentially
      const initialValue = uploadedFiles[key];
      const newFiles = value.documents_uploaded.filter(
        (file) => !file.uploaded_document_id
      );

      if (newFiles.length > 0) {
        if (
          !hasPermission(
            profile.permissions.actions,
            actionType.documents_upload
          )
        ) {
          toast.error("You do not have permission to upload documents");
          return;
        }
        await createDocumentAPI(
          {
            ...value,
            documents_uploaded: newFiles,
          },
          key
        );
      }
      if (
        value.signature_required !== initialValue.signature_required &&
        value.documents_uploaded.length > 0 &&
        value.document_id
      ) {
        if (
          !hasPermission(
            profile.permissions.actions,
            actionType.documents_require_signature
          )
        ) {
          toast.error("You do not have permission to update signature");
          return;
        }
        await updateSignatureAPI(value.document_id, value.signature_required);
      }
    }
    if (values.docStatus) {
      const res = await updateDocumentStatusAPI();
      if (res.success) {
        if (role === userRoles.Document_Controller) {
          toast.success("Sent to QC Coordinator for E-Signature.");
        } else if (role === userRoles.QC_Coordinator) {
          toast.success("All Documents Signed.");
        } else {
          toast.success("Documents Status Updated");
        }
      } else {
        toast.error(res.error.message || "Something went wrong");
      }
    } else {
      toast.success("Documents Uploaded");
    }
    resetForm();
    setSubmitting(false);
    getAllUploadedDocAPI();
    getRFIDetails(rfi_id);
  };

  const formik = useFormik({
    initialValues: {
      documents: uploadedFiles,
      docStatus: docStatus === "All_Uploaded",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, { setSubmitting, resetForm }) =>
      handleSubmit(values, { setSubmitting, resetForm }),
    enableReinitialize: true,
  });

  const handleFileChange = (event, docName) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const currentFiles =
      formik.values.documents[docName]?.documents_uploaded || [];
    const totalFilesAfterUpload = currentFiles.length + files.length;

    if (totalFilesAfterUpload > MAX_FILES_PER_DOCUMENT) {
      formik.setFieldError(
        `documents.${docName}.documents_uploaded`,
        `Maximum ${MAX_FILES_PER_DOCUMENT} files allowed`
      );
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      // File type validation
      const allowedExtensions = [".pdf"];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        invalidFiles.push(file.name);
        return;
      }

      // File size validation
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(`${file.name} (file too large)`);
        return;
      }

      validFiles.push({
        file,
        fileName: file.name,
        id: `${docName}-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      });
    });

    if (invalidFiles.length > 0) {
      formik.setFieldError(
        `documents.${docName}.documents_uploaded`,
        `Invalid files: ${invalidFiles.join(", ")}`
      );
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...currentFiles, ...validFiles];
      formik.setFieldValue(
        `documents.${docName}.documents_uploaded`,
        updatedFiles
      );

      // Clear any previous errors if files are valid
      if (formik.errors.documents?.[docName]?.documents_uploaded) {
        formik.setFieldError(
          `documents.${docName}.documents_uploaded`,
          undefined
        );
      }

      // Clear the overall form error if at least one document has files
      if (
        formik.errors.documents &&
        Object.values(formik.values.documents).some(
          (doc) => doc.documents_uploaded.length > 0
        )
      ) {
        formik.setFieldError("documents", undefined);
      }
    }

    // Clear the input to allow selecting same files again
    event.target.value = "";
  };

  const handleRemoveFile = async (docName, file) => {
    try {
      if (
        !hasPermission(profile.permissions.actions, actionType.documents_delete)
      ) {
        toast.error("You do not have permission to delete documents");
        return;
      }
      if (file.uploaded_document_id) {
        await deleteDocumentAPI(file.uploaded_document_id);
      }

      // Only update local state after successful API call
      const updatedFiles = formik.values.documents[
        docName
      ].documents_uploaded.filter((fl) => fl.id !== file.id);

      formik.setFieldValue(
        `documents.${docName}.documents_uploaded`,
        updatedFiles
      );
      toast.success("File removed successfully");

      // Clear error if no files remain and error exists
      if (
        updatedFiles.length === 0 &&
        formik.errors.documents?.[docName]?.documents_uploaded
      ) {
        formik.setFieldError(
          `documents.${docName}.documents_uploaded`,
          undefined
        );
      }
      return true;
    } catch (error) {
      console.error("Failed to remove file:", error);
      toast.error("Failed to remove file. Please try again.");
      // Local state is NOT updated since the API call failed
      return false;
    }
  };

  const handleCheckboxChange = (event, docName) => {
    const { checked } = event.target;
    formik.setFieldValue(`documents.${docName}.signature_required`, checked);
  };

  const handleDocumentStatusChange = (e) => {
    const { checked } = e.target;
    formik.setFieldValue(`docStatus`, checked);
  };

  const isUploadDisabled = (docName) => {
    const currentFiles =
      formik.values.documents[docName]?.documents_uploaded || [];
    return currentFiles.length >= MAX_FILES_PER_DOCUMENT;
  };

  const getAllUploadedDocAPI = useCallback(() => {
    setLoading(true);
    api
      .get(`/api/rfi-documents/document-urls?rfi_id=${rfi_id}`)
      .then((res) => {
        const documentData = res.data.data;
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        let allDocSignedCheck = true;
        const finalDataForDocuments = listOfDocuments.reduce((acc, docName) => {
          const findDocument = documentData.find(
            (doc) => doc.document_key === docName
          );

          acc[docName] = {
            ...findDocument,
            documents_uploaded:
              findDocument?.documents_uploaded.map((doc, i) => {
                if (findDocument?.signature_required && !doc.esigned) {
                  allDocSignedCheck = false;
                }
                return {
                  file: `${baseUrl}${doc.document_url}`,
                  fileName: doc.filename || `${docName} ${i + 1}`,
                  id: doc.uploaded_document_id,
                  uploaded_document_id: doc.uploaded_document_id,
                  original_document_url:
                    `${baseUrl}${doc.original_document_url}` || null,
                  signed: doc.esigned || false,
                };
              }) || [],
            signature_required: findDocument?.signature_required || false,
          };
          return acc;
        }, {});

        setUploadedFiles(finalDataForDocuments);
        setAllDocSigned(allDocSignedCheck);
      })
      .catch((err) => {
        console.error("Error fetching uploaded files:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [rfi_id]);

  const createDocumentAPI = async (document_values, document_key) => {
    if (!rfi_id) {
      toast.error("RFI ID not found");
      return;
    }

    const files = document_values.documents_uploaded.map((file) => file.file);

    const formData = convertToFormData({
      rfi_id,
      documents: files,
      document_key: document_key,
      signature_required: document_values.signature_required,
    });
    return await api
      .post(`/api/rfi-documents/upload`, formData)
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.error("Error creating document:", err);
        return false;
      });
  };

  const deleteDocumentAPI = async (document_id) => {
    if (!document_id) {
      toast.error("Document ID not found");
      return;
    }
    if (!rfi_id) {
      toast.error("RFI ID not found");
      return;
    }
    return await api
      .delete(`/api/rfi-documents`, {
        data: {
          uploaded_document_id: document_id,
          rfi_id: rfi_id,
        },
      })
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.error("Error deleting document:", err);
        toast.error("Error deleting document");
        return false;
      });
  };

  const updateSignatureAPI = async (document_id, signature_required) => {
    if (!document_id) {
      toast.error("Document ID not found");
      return;
    }
    return await api
      .put(`/api/rfi-documents/signature-required`, {
        document_id: document_id,
        signature_required: signature_required,
      })
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.error("Error updating document signature:", err);
        return false;
      });
  };

  const updateDocumentStatusAPI = async () => {
    return await api
      .post(`/api/rfi-documents/request-esignature`, { rfi_id: rfi_id })
      .then((res) => {
        return { success: true, data: res.data };
      })
      .catch((err) => {
        console.error("Error updating document status:", err.response.data);
        return { success: false, error: err.response.data };
      });
  };

  useEffect(() => {
    if (!rfi_id) return;
    if (
      !hasPermission(profile.permissions.actions, actionType.documents_view)
    ) {
      navigate("/dashboard");
      return;
    }
    getAllUploadedDocAPI();
  }, [getAllUploadedDocAPI, navigate, rfi_id, profile.permissions.actions]);

  // Load PDF from file or URL
  const loadPDF = async (file) => {
    if (
      !hasPermission(profile.permissions.actions, actionType.documents_sign)
    ) {
      toast.error("You do not have permission to sign documents");
      return;
    }

    const source = file.original_document_url || file.file;
    setSelectedDoc(file);
    setEsignUrlLoading(true);
    setError("");
    setStartEsign(true);

    try {
      // Dynamically load PDF.js if not already loaded
      if (!window["pdfjs-dist/build/pdf"]) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const pdfjsLib = window["pdfjs-dist/build/pdf"];
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

      let pdfData;
      if (typeof source === "string") {
        pdfData = await pdfjsLib.getDocument(source).promise;
      } else {
        const reader = new FileReader();
        pdfData = await new Promise((resolve, reject) => {
          reader.onload = async (e) => {
            try {
              const pdf = await pdfjsLib.getDocument(e.target.result).promise;
              resolve(pdf);
            } catch (err) {
              reject(err);
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(source);
        });
      }

      const pages = [];
      for (let i = 1; i <= pdfData.numPages; i++) {
        const page = await pdfData.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        pages.push(canvas.toDataURL());
      }

      setPdfPages(pages);
      setCurrentPage(0);
      setSignatures([]);
      // setStartEsign(true);
    } catch (err) {
      setError("Failed to load PDF. Please check the file or URL.");
      setPdfLoadingError(true);
      console.error(err);
      toast.error(
        err.message || "Failed to load PDF. Please check the file or URL."
      );
    }
    setEsignUrlLoading(false);
  };

  //Save pdf to database
  const savePdfToDatabase = async (formData) => {
    if (
      !hasPermission(profile.permissions.actions, actionType.documents_sign)
    ) {
      toast.error("You do not have permission to sign documents");
      return;
    }
    return await api
      .post("/api/rfi-documents/esign", formData)
      .then((res) => {
        return { success: true, data: res.data, message: res.message };
      })
      .catch((err) => {
        console.error("Error saving PDF to database:", err);
        return { success: false, message: err.response.data.message };
      });
  };

  // Reset esign page and move to all documents section
  const resetFcn = () => {
    setStartEsign(false);
    setPdfPages([]);
    setCurrentPage(0);
    setSignatures([]);
    setError("");
    setEsignUrlLoading(false);
    getAllUploadedDocAPI();
  };

  return {
    formik,
    loading,
    file,
    deleteModal,
    startEsign,
    allDocSigned,
    handleFileChange,
    handleRemoveFile,
    handleCheckboxChange,
    handleDocumentStatusChange,
    isUploadDisabled,
    setDeleteModal,
    setFile,
    setStartEsign,
    esignUrlLoading,
    loadPDF,
    error,
    pdfPages,
    currentPage,
    signatures,
    setPdfPages,
    setError,
    setCurrentPage,
    setSignatures,
    setEsignUrlLoading,
    resetFcn,
    savePdfToDatabase,
    selectedDoc,
    pdfLoadingError,
  };
}
